/*
  ContourTrace — traces the outer silhouette of an image's alpha channel and
  returns a simplified polygon / SVG path. Used client-side for the live
  die-cut preview, and reused server-side (Node) to regenerate a cleaner
  production cut path at order time.

  No dependencies, no build step — plain JS, works as a browser <script> or
  a Node require().

  Algorithm:
    1. Threshold the alpha channel into a binary foreground/background mask.
    2. Moore-neighbor boundary tracing to walk the outer edge of the largest
       foreground region into an ordered list of pixel points.
    3. Douglas-Peucker simplification to cut the point count down to
       something that prints as a smooth die-cut line instead of a jagged
       pixel outline.
    4. Optional outward offset (a few px) so the cut line clears the artwork
       edge slightly, the way real die-cut stickers do.

  Limitation: this assumes a single connected foreground shape (typical for
  sticker artwork). Multi-blob artwork (e.g. text with separate letters)
  will only trace the largest blob — good enough for a live preview, but the
  production regeneration step should flag multi-blob artwork for a human
  check rather than silently dropping the smaller pieces.
*/
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.ContourTrace = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function buildMask(imageData, alphaThreshold) {
    var w = imageData.width,
      h = imageData.height,
      data = imageData.data,
      mask = new Uint8Array(w * h);
    for (var i = 0; i < w * h; i++) {
      mask[i] = data[i * 4 + 3] >= alphaThreshold ? 1 : 0;
    }
    return { mask: mask, width: w, height: h };
  }

  // Flood-fill to find the largest connected foreground region, zero out the rest.
  function keepLargestRegion(maskInfo) {
    var mask = maskInfo.mask,
      w = maskInfo.width,
      h = maskInfo.height,
      visited = new Uint8Array(w * h),
      best = null,
      bestSize = 0;

    for (var start = 0; start < mask.length; start++) {
      if (!mask[start] || visited[start]) continue;
      var stack = [start],
        region = [];
      visited[start] = 1;
      while (stack.length) {
        var idx = stack.pop();
        region.push(idx);
        var x = idx % w,
          y = (idx / w) | 0;
        var neighbors = [
          [x - 1, y],
          [x + 1, y],
          [x, y - 1],
          [x, y + 1],
        ];
        for (var n = 0; n < 4; n++) {
          var nx = neighbors[n][0],
            ny = neighbors[n][1];
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          var nIdx = ny * w + nx;
          if (mask[nIdx] && !visited[nIdx]) {
            visited[nIdx] = 1;
            stack.push(nIdx);
          }
        }
      }
      if (region.length > bestSize) {
        bestSize = region.length;
        best = region;
      }
    }

    var cleaned = new Uint8Array(w * h);
    if (best) {
      for (var j = 0; j < best.length; j++) cleaned[best[j]] = 1;
    }
    return { mask: cleaned, width: w, height: h };
  }

  function findStartPixel(maskInfo) {
    var mask = maskInfo.mask,
      w = maskInfo.width;
    for (var i = 0; i < mask.length; i++) {
      if (mask[i]) return { x: i % w, y: (i / w) | 0 };
    }
    return null;
  }

  // Moore-neighbor boundary tracing: walk clockwise around the outer edge.
  function traceBoundary(maskInfo) {
    var mask = maskInfo.mask,
      w = maskInfo.width,
      h = maskInfo.height;

    function isForeground(x, y) {
      if (x < 0 || y < 0 || x >= w || y >= h) return false;
      return mask[y * w + x] === 1;
    }

    var start = findStartPixel(maskInfo);
    if (!start) return [];

    // 8-connected clockwise neighbor offsets, starting "west" of current pixel.
    var dirs = [
      [-1, 0], [-1, -1], [0, -1], [1, -1],
      [1, 0], [1, 1], [0, 1], [-1, 1],
    ];

    var points = [];
    var current = start;
    // `start` is the topmost-leftmost foreground pixel (raster-scan order),
    // so its west neighbor is guaranteed background — that's the pixel we
    // treat ourselves as having "arrived from".
    var incomingDir = 0; // direction, from `current`, pointing back to the background pixel we arrived from
    var maxSteps = w * h * 4; // safety cap against pathological masks
    var steps = 0;

    do {
      points.push({ x: current.x, y: current.y });
      // Scan clockwise starting just past the incoming direction.
      var searchStart = (incomingDir + 1) % 8;
      var found = false;
      for (var k = 0; k < 8; k++) {
        var dIdx = (searchStart + k) % 8;
        var d = dirs[dIdx];
        var nx = current.x + d[0],
          ny = current.y + d[1];
        if (isForeground(nx, ny)) {
          current = { x: nx, y: ny };
          // The new pixel's "incoming" direction points back toward the
          // pixel we just left — i.e. the opposite of the direction we
          // just moved in.
          incomingDir = (dIdx + 4) % 8;
          found = true;
          break;
        }
      }
      if (!found) break; // isolated pixel
      steps++;
    } while ((current.x !== start.x || current.y !== start.y) && steps < maxSteps);

    return points;
  }

  function perpendicularDistance(pt, lineStart, lineEnd) {
    var dx = lineEnd.x - lineStart.x,
      dy = lineEnd.y - lineStart.y;
    var len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) {
      var ddx = pt.x - lineStart.x,
        ddy = pt.y - lineStart.y;
      return Math.sqrt(ddx * ddx + ddy * ddy);
    }
    var t = ((pt.x - lineStart.x) * dx + (pt.y - lineStart.y) * dy) / (len * len);
    var projX = lineStart.x + t * dx,
      projY = lineStart.y + t * dy;
    var distX = pt.x - projX,
      distY = pt.y - projY;
    return Math.sqrt(distX * distX + distY * distY);
  }

  // Douglas-Peucker polygon simplification.
  function simplify(points, epsilon) {
    if (points.length < 3) return points;
    var dmax = 0,
      index = 0,
      end = points.length - 1;
    for (var i = 1; i < end; i++) {
      var d = perpendicularDistance(points[i], points[0], points[end]);
      if (d > dmax) {
        index = i;
        dmax = d;
      }
    }
    if (dmax > epsilon) {
      var left = simplify(points.slice(0, index + 1), epsilon);
      var right = simplify(points.slice(index), epsilon);
      return left.slice(0, -1).concat(right);
    }
    return [points[0], points[end]];
  }

  // Push every point outward from the shape's centroid by `offset` px —
  // approximates the small clearance real die-cut lines leave around artwork.
  function offsetOutward(points, offset) {
    if (!offset) return points;
    var cx = 0,
      cy = 0;
    for (var i = 0; i < points.length; i++) {
      cx += points[i].x;
      cy += points[i].y;
    }
    cx /= points.length;
    cy /= points.length;
    return points.map(function (p) {
      var dx = p.x - cx,
        dy = p.y - cy;
      var len = Math.sqrt(dx * dx + dy * dy) || 1;
      return { x: p.x + (dx / len) * offset, y: p.y + (dy / len) * offset };
    });
  }

  function toSvgPath(points) {
    if (!points.length) return '';
    var d = 'M ' + points[0].x.toFixed(1) + ' ' + points[0].y.toFixed(1);
    for (var i = 1; i < points.length; i++) {
      d += ' L ' + points[i].x.toFixed(1) + ' ' + points[i].y.toFixed(1);
    }
    return d + ' Z';
  }

  /**
   * @param {ImageData} imageData - from canvas getImageData()
   * @param {Object} [opts]
   * @param {number} [opts.alphaThreshold=16] - 0-255, alpha values at/above this count as foreground
   * @param {number} [opts.simplifyTolerance=1.5] - px, higher = smoother/fewer points
   * @param {number} [opts.offset=4] - px, outward clearance from the artwork edge
   * @returns {{points: {x:number,y:number}[], path: string}}
   */
  function trace(imageData, opts) {
    opts = opts || {};
    var alphaThreshold = opts.alphaThreshold != null ? opts.alphaThreshold : 16;
    var simplifyTolerance = opts.simplifyTolerance != null ? opts.simplifyTolerance : 1.5;
    var offset = opts.offset != null ? opts.offset : 4;

    var maskInfo = keepLargestRegion(buildMask(imageData, alphaThreshold));
    var raw = traceBoundary(maskInfo);
    var simplified = simplify(raw, simplifyTolerance);
    var offsetPoints = offsetOutward(simplified, offset);

    return { points: offsetPoints, path: toSvgPath(offsetPoints) };
  }

  return {
    trace: trace,
    // exposed for testing / server-side reuse without a full ImageData object
    _internal: { buildMask: buildMask, keepLargestRegion: keepLargestRegion, traceBoundary: traceBoundary, simplify: simplify, offsetOutward: offsetOutward, toSvgPath: toSvgPath },
  };
});
